/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { Link, useRouterState } from '@tanstack/react-router'
import type { MouseEvent, ReactElement } from 'react'
import { forwardRef, useMemo } from 'react'

import { NavBadge } from '@/components/blocks/nav-badge'
import { cn } from '@/lib/utils'

export interface BottomNavActionProps {
  icon: ReactElement
  label: string
  to?: string
  onClick?: (event: MouseEvent) => void
  badge?: number | boolean
  isActive?: boolean | ((location: { pathname: string }) => boolean)
}

export const BottomNavAction = forwardRef<HTMLDivElement, BottomNavActionProps>(function BottomNavAction(props, ref) {
  const { icon, label, to, onClick, badge, isActive: isActiveProp } = props
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  const active = useMemo(() => {
    if (typeof isActiveProp === 'boolean') {
      return isActiveProp
    }
    if (typeof isActiveProp === 'function') {
      return isActiveProp({ pathname: currentPath })
    }
    if (to) {
      return currentPath === to || currentPath.startsWith(`${to}/`)
    }
    return false
  }, [isActiveProp, currentPath, to])

  const content = (
    <div
      className={cn(
        'flex min-w-0 max-w-20 cursor-pointer flex-col items-center justify-center px-1 py-1 transition-colors',
        active ? 'text-primary' : 'over:text-foreground text-muted-foreground'
      )}
      ref={ref}
    >
      <div className="relative inline-flex [&_svg]:size-[22px]">
        {icon}
        <NavBadge badge={badge} size="md" />
      </div>
      <span
        className={cn(
          'mt-0.5 max-w-full truncate text-center text-[10px] leading-tight',
          active ? 'font-semibold' : 'font-medium'
        )}
      >
        {label}
      </span>
    </div>
  )

  if (to) {
    return (
      <Link className="flex min-w-0 flex-1 items-center justify-center no-underline" onClick={onClick} to={to}>
        {content}
      </Link>
    )
  }

  return (
    <button
      className="flex min-w-0 flex-1 items-center justify-center border-none bg-transparent p-0"
      onClick={onClick}
      type="button"
    >
      {content}
    </button>
  )
})

BottomNavAction.displayName = 'BottomNavAction'
